import {Fragment, useEffect, useMemo, useState} from "react";
import {DialogPrimitive as Dialog} from "ch3-ui-lib";
import {PhoneHQGlyph, PhoneHQLoader, PhoneHQButton} from 'ch3-ui-lib';
import {useNavigate} from "react-router-dom";
import {
    useCreateEnsAlertMutation,
    useGenerateEnsTtsMutation,
    useGetEnsConfigurationQuery,
    useGetEnsPresetsQuery,
} from "../../../store/features/ens/ensService";
import {
    EnsAlertType,
    EnsAlertTemplate,
    EnsLevelInputDto,
    EnsTtsRequest,
    EnsUserContactListItem,
    EnsVoicePrompt,
} from "../../../store/features/ens/types";
import {
    buildCreateAlertRequest,
    createDefaultVoicePromptDraft,
    hasAlertTargets,
    isVoicePromptDraftValid,
    normalizeVoicePromptDraft,
    levelsRequireExternalMsisdn,
    normalizeEscalationLevels,
    SubmitBlockedError,
    toSubmitBlockedError,
} from "./utils";
import {Step1Type} from "./Step1Type";
import {Step2Target} from "./Step2Target";
import {Step3Escalation} from "./Step3Escalation";
import {Step4Review} from "./Step4Review";
import {TemplatePickerOverlay} from "./StartFromTemplateStep";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    initialTemplate?: EnsAlertTemplate | null;
    onInitialTemplateConsumed?: () => void;
    onManageTemplates: () => void;
}

interface WizardState {
    type: EnsAlertType | null;
    name: string;
    message: string;
    targetGroupIds: number[];
    targetUsers: EnsUserContactListItem[];
    levels: EnsLevelInputDto[];
    voicePrompt: EnsVoicePrompt | null;
    voicePromptDraft: EnsTtsRequest;
    voicePromptGeneratedFromDraft: boolean;
}

const createInitialState = (): WizardState => ({
    type: null,
    name: '',
    message: '',
    targetGroupIds: [],
    targetUsers: [],
    levels: [],
    voicePrompt: null,
    voicePromptDraft: createDefaultVoicePromptDraft(),
    voicePromptGeneratedFromDraft: false,
});

const WIZARD_STEPS = [
    {
        key: "type" as const,
        isValid: (s: WizardState) => s.type != null,
    },
    {
        key: "target" as const,
        isValid: (s: WizardState) =>
            s.name.trim().length > 0
            && s.message.trim().length > 0
            && hasAlertTargets(s.targetGroupIds, s.targetUsers.map((user) => user.userId))
            && (s.voicePrompt != null || isVoicePromptDraftValid(s.voicePromptDraft)),
    },
    {
        key: "escalation" as const,
        isValid: (s: WizardState) =>
            s.levels.length > 0
            && s.levels.every((l) => l.delaySecondsFromPrevious >= 0)
            && s.voicePrompt != null,
    },
    {
        key: "review" as const,
        isValid: () => true,
    },
];

const stateFromTemplate = (template: EnsAlertTemplate): WizardState => ({
    type: template.type,
    name: template.name,
    message: template.message,
    targetGroupIds: template.targetGroupIds,
    targetUsers: template.targetUsers,
    levels: normalizeEscalationLevels(template.levels),
    voicePrompt: template.voicePrompt,
    voicePromptDraft: createDefaultVoicePromptDraft(),
    voicePromptGeneratedFromDraft: false,
});

const StepDots = ({step}: { step: number }) => (
    <div className="hidden shrink-0 items-center gap-1.5 sm:flex">
        {WIZARD_STEPS.map((_, idx) => {
            const isDone = idx < step;
            const isCurrent = idx === step;
            return (
                <Fragment key={idx}>
                    <div
                        className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold transition-colors ${
                            isDone || isCurrent
                                ? 'border border-brand bg-brand text-white'
                                : 'border border-line bg-zinc-100 text-ink-muted'
                        }`}
                    >
                        {isDone ? <PhoneHQGlyph name="check" size={14}/> : idx + 1}
                    </div>
                    {idx < WIZARD_STEPS.length - 1 && (
                        <div className={`h-0.5 w-5 rounded-full transition-colors ${isDone ? 'bg-brand' : 'bg-fill-subtle'}`}/>
                    )}
                </Fragment>
            );
        })}
    </div>
);

const TemplateStartButton = ({onClick}: { onClick: () => void }) => (
    <button
        phq-id="ensWizard-openTemplatePickerBtn"
        type="button"
        onClick={onClick}
        className="mb-4 flex min-w-0 items-center gap-3 rounded-lg border border-dashed border-brand-focus bg-brand-tint-subtle px-4 py-3 text-left transition-colors hover:border-brand hover:bg-brand-tint focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-focus"
    >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-brand-tint bg-surface text-brand-ink">
            <PhoneHQGlyph name="description" size={18}/>
        </span>
        <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-brand-ink">Start from a template</span>
            <span className="mt-0.5 block text-xs leading-5 text-brand-ink">
                Pre-fill the name and all escalation steps from a saved template.
            </span>
        </span>
        <PhoneHQGlyph name="keyboard_arrow_right" size={20} className="shrink-0 text-brand-ink"/>
    </button>
);

export const CreateAlertWizard = ({
    isOpen,
    onClose,
    initialTemplate,
    onInitialTemplateConsumed,
    onManageTemplates,
}: Props) => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [state, setState] = useState<WizardState>(createInitialState);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);
    const [appliedTemplateName, setAppliedTemplateName] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<SubmitBlockedError | null>(null);

    const {data: presets, isLoading: presetsLoading} = useGetEnsPresetsQuery(undefined, {skip: !isOpen});
    const {data: ensConfiguration} = useGetEnsConfigurationQuery(undefined, {skip: !isOpen});
    const [createAlert] = useCreateEnsAlertMutation();
    const [generateVoicePrompt, {isLoading: isGeneratingVoicePrompt}] = useGenerateEnsTtsMutation();

    const presetByType = useMemo(() => {
        const map = new Map<EnsAlertType, EnsLevelInputDto[]>();
        (presets ?? []).forEach((p) => map.set(p.type, p.levels));
        return map;
    }, [presets]);

    useEffect(() => {
        if (!isOpen) {
            setStep(0);
            setState(createInitialState());
            setShowTemplatePicker(false);
            setAppliedTemplateName(null);
            setSubmitting(false);
            setSubmitError(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !initialTemplate) return;
        setState(stateFromTemplate(initialTemplate));
        setShowTemplatePicker(false);
        setAppliedTemplateName(initialTemplate.name);
        setStep(0);
        onInitialTemplateConsumed?.();
    }, [initialTemplate, isOpen, onInitialTemplateConsumed]);

    useEffect(() => {
        if (!state.type || state.levels.length !== 0) return;
        const preset = presetByType.get(state.type);
        if (!preset) return;
        setState((prev) => ({...prev, levels: normalizeEscalationLevels(preset)}));
    }, [state.type, presetByType, state.levels.length]);

    const smsSenderConfigured = ensConfiguration?.smsSenderMsisdn != null;
    const smsLevelBlocked = !smsSenderConfigured && state.levels.some((level) => level.channel === 'SMS');
    const currentStepKey = WIZARD_STEPS[step]?.key;
    const baseStepValid = WIZARD_STEPS[step]?.isValid(state) ?? false;
    const isValid = baseStepValid && !(smsLevelBlocked && (currentStepKey === 'escalation' || currentStepKey === 'review'));

    const goNext = async () => {
        if (currentStepKey === 'target' && state.voicePrompt == null) {
            if (!isVoicePromptDraftValid(state.voicePromptDraft)) return;
            try {
                const voicePrompt = await generateVoicePrompt(
                    normalizeVoicePromptDraft(state.voicePromptDraft),
                ).unwrap();
                setState((previous) => ({
                    ...previous,
                    voicePrompt,
                    voicePromptGeneratedFromDraft: true,
                }));
            } catch {
                // toast handled in service; stay on Step 2 so the user can retry
                return;
            }
        }

        if (step === WIZARD_STEPS.length - 1) {
            await handleSubmit();
        } else {
            setStep((s) => s + 1);
        }
    };

    const goBack = () => {
        setSubmitError(null);
        if (step === 0) {
            onClose();
        } else {
            setStep((s) => s - 1);
        }
    };

    const handleUseTemplate = (template: EnsAlertTemplate) => {
        setState(stateFromTemplate(template));
        setAppliedTemplateName(template.name);
        setShowTemplatePicker(false);
        setStep(0);
    };

    const handleManageTemplates = () => {
        setShowTemplatePicker(false);
        onManageTemplates();
    };

    const handleSubmit = async () => {
        if (!state.type) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            const result = await createAlert(buildCreateAlertRequest({
                type: state.type,
                name: state.name,
                message: state.message,
                targetGroupIds: state.targetGroupIds,
                targetUserIds: state.targetUsers.map((user) => user.userId),
                levels: state.levels,
                voicePrompt: state.voicePrompt,
            })).unwrap();
            onClose();
            navigate(`/admin/ens/alerts/${result.summary.publicId}`);
        } catch (error) {
            setSubmitError(toSubmitBlockedError(error));
        } finally {
            setSubmitting(false);
        }
    };

    const stepContent = () => {
        if (presetsLoading) {
            return (
                <div className="flex items-center justify-center py-10">
                    <PhoneHQLoader/>
                </div>
            );
        }
        switch (WIZARD_STEPS[step]?.key) {
            case "type":
                return (
                    <>
                        <TemplateStartButton onClick={() => setShowTemplatePicker(true)}/>
                        {appliedTemplateName && (
                            <div className="mb-4 flex items-center gap-2 rounded-lg border border-brand-tint bg-brand-tint-subtle px-4 py-3 text-sm font-semibold text-brand-ink">
                                <PhoneHQGlyph name="description" size={16}/>
                                <span>Template applied: “{appliedTemplateName}” — name and escalation timeouts pre-filled.</span>
                            </div>
                        )}
                        <Step1Type
                            type={state.type}
                            presetByType={presetByType}
                            onChange={(type) => setState((prev) => ({
                                ...prev,
                                type,
                                levels: normalizeEscalationLevels(presetByType.get(type) ?? prev.levels),
                            }))}
                        />
                    </>
                );
            case "target":
                return (
                    <Step2Target
                        name={state.name}
                        message={state.message}
                        targetGroupIds={state.targetGroupIds}
                        targetUsers={state.targetUsers}
                        voicePrompt={state.voicePrompt}
                        voicePromptDraft={state.voicePromptDraft}
                        autoPreviewVoicePrompt={state.voicePromptGeneratedFromDraft}
                        externalNumberRequired={levelsRequireExternalMsisdn(state.levels)}
                        onChange={(patch) => setState((prev) => ({...prev, ...patch}))}
                        onVoicePromptChange={(voicePrompt) =>
                            setState((prev) => ({
                                ...prev,
                                voicePrompt,
                                voicePromptGeneratedFromDraft: voicePrompt != null
                                    && prev.voicePromptGeneratedFromDraft,
                            }))
                        }
                        onVoicePromptDraftChange={(voicePromptDraft) =>
                            setState((prev) => ({
                                ...prev,
                                voicePromptDraft,
                                voicePrompt: null,
                                voicePromptGeneratedFromDraft: false,
                            }))
                        }
                    />
                );
            case "escalation":
                return (
                    <Step3Escalation
                        levels={state.levels}
                        onChange={(levels) => setState((prev) => ({...prev, levels: normalizeEscalationLevels(levels)}))}
                        voicePromptSet={state.voicePrompt != null}
                        smsSenderConfigured={smsSenderConfigured}
                    />
                );
            case "review":
                return <Step4Review state={state} submitError={submitError}/>;
            default:
                return null;
        }
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={(open) => (!open ? onClose() : undefined)}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-50 bg-scrim data-[state=open]:animate-overlayShow"/>
                <Dialog.Content
                    onPointerDownOutside={(event) => event.preventDefault()}
                    className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[720px] max-w-[calc(100vw-24px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl bg-surface shadow-[0_20px_60px_rgba(0,0,0,0.18)] sm:max-w-[calc(100vw-48px)]"
                >
                    <div className="flex min-w-0 items-center justify-between gap-4 border-b border-line px-4 py-4 sm:grid sm:grid-cols-[1fr_auto_1fr] sm:px-8">
                        <div className="min-w-0">
                            <Dialog.Title className="text-base font-semibold text-ink">Create alert</Dialog.Title>
                            <div className="mt-0.5 text-xs text-ink-muted">
                                Step {step + 1} of {WIZARD_STEPS.length}
                            </div>
                        </div>
                        <StepDots step={step}/>
                        <div className="flex justify-end">
                            <Dialog.Close asChild>
                                <button
                                    aria-label="Close"
                                    className="flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-100 text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink"
                                >
                                    <PhoneHQGlyph name="close" size={16}/>
                                </button>
                            </Dialog.Close>
                        </div>
                    </div>
                    <div className="flex min-h-0 flex-1 justify-center overflow-y-auto overflow-x-hidden px-4 py-6 sm:px-8">
                        <div className="w-full min-w-0">
                            {stepContent()}
                        </div>
                    </div>
                    <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-4 sm:px-8">
                        <PhoneHQButton
                            phq-id="ensWizard-backBtn"
                            variant="secondary"
                            size="sm"
                            onClick={goBack}
                            disabled={submitting || isGeneratingVoicePrompt}
                        >
                            {step === 0 ? 'Cancel' : 'Back'}
                        </PhoneHQButton>
                        <PhoneHQButton
                            phq-id="ensWizard-nextBtn"
                            variant="primary"
                            size="sm"
                            onClick={goNext}
                            disabled={!isValid || submitting || isGeneratingVoicePrompt}
                        >
                            {isGeneratingVoicePrompt
                                ? 'Generating voice…'
                                : step === WIZARD_STEPS.length - 1
                                    ? (submitting ? 'Sending…' : 'Send alert')
                                    : 'Continue'}
                        </PhoneHQButton>
                    </div>
                    {showTemplatePicker && (
                        <TemplatePickerOverlay
                            onClose={() => setShowTemplatePicker(false)}
                            onManageTemplates={handleManageTemplates}
                            onUseTemplate={handleUseTemplate}
                        />
                    )}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};
